import { useRef, useState } from 'react';
import { SUPPORTED_LANGUAGES, useLanguage } from './language';
import './LanguageSelector.css';

/**
 * Dropdown language picker.
 * Props:
 *   variant — 'icon' (just flag + chevron, compact) | 'full' (flag + label)
 *   placement — 'bottom-start' | 'bottom-end' (default)
 */
export default function LanguageSelector({ variant = 'full', placement = 'bottom-end' }) {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = SUPPORTED_LANGUAGES.find(l => l.code === language) ?? SUPPORTED_LANGUAGES[0];

  const handleSelect = (code) => {
    setLanguage(code);
    setOpen(false);
  };

  // Close on outside click
  const handleBlur = (e) => {
    if (!ref.current?.contains(e.relatedTarget)) setOpen(false);
  };

  return (
    <div
      className={`lang-sel lang-sel--${variant}`}
      ref={ref}
      onBlur={handleBlur}
    >
      <button
        type="button"
        className="lang-sel__trigger"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="lang-sel__flag">{current.flag}</span>
        <span className="lang-sel__label">{current.code.toUpperCase()}</span>
        <span className={`lang-sel__chevron${open ? ' lang-sel__chevron--open' : ''}`}>▾</span>
      </button>

      {open && (
        <ul
          className={`lang-sel__menu lang-sel__menu--${placement}`}
          role="listbox"
          aria-label="Select language"
        >
          {SUPPORTED_LANGUAGES.map(lang => (
            <li
              key={lang.code}
              role="option"
              aria-selected={lang.code === language}
              className={`lang-sel__option${lang.code === language ? ' lang-sel__option--active' : ''}`}
              onMouseDown={() => handleSelect(lang.code)}
            >
              <span className="lang-sel__flag">{lang.flag}</span>
              <span className="lang-sel__option-label">{lang.code.toUpperCase()}</span>
              {lang.code === language && <span className="lang-sel__check">✓</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
